import { Button, Col, Row, Spin } from 'antd';
import { FullScreenModal } from 'components/Signature/styles';
import { useCallback, useDeferredValue, useRef, useState, useEffect } from 'react';
import useScreenHeight from 'hooks/useScreenHeight';
import html2pdf from 'html2pdf.js';
import { DownloadOutlined } from '@ant-design/icons';
import { blobToFile } from 'utils/common';
import {
  ButtonWrapper,
  A4Container,
  StyledStatisticTable,
  HeaderContainer,
  HeaderColumn,
  TitleContainer,
  SignArea,
  FooterContainer,
  UserInfoContainer,
  TableContainer,
  CheckboxContainer,
  HiddenContainer
} from './style';
import { ColumnsType } from 'antd/lib/table';
import { Field } from 'components/Field';
import CheckboxWithTitleForPdf from 'components/Checkbox/CheckboxWithTitleForPdf';
import { useSelector } from 'react-redux';
import { selectAuthUser } from 'store/user/shared/selectors';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { PGD_NAME } from 'config';

export const CommentContainer = styled('p')`
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #ced1e9;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: break-word;
`;

interface ModalViewPDFSignExportProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (file?: File) => void;
  reviewerComment: any;
  columns: ColumnsType<any>;
  viewData: any[];
  data: any;
  isCv1: boolean;
}

function ModalGenerate({
  open,
  data,
  columns,
  viewData,
  reviewerComment,
  onClose,
  onSuccess,
  isCv1
}: ModalViewPDFSignExportProps): JSX.Element {
  const user = useSelector(selectAuthUser);

  const [loadingDownload, setLoadingDownload] = useState<boolean>(false);
  const [loadingInit, setLoadingInit] = useState<boolean>(true);

  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string>('');

  const hiddenRef = useRef<HTMLDivElement>(null);
  const height = useScreenHeight();
  const deferredLoading = useDeferredValue(loadingDownload || loadingInit);

  useEffect(() => {
    setLoadingInit(true);
    const timer = setTimeout(() => {
      setLoadingInit(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, [data]);

  const handleGeneratePdf = useCallback(async () => {
    if (!hiddenRef.current) return;
    setLoadingDownload(true);

    try {
      const option = {
        margin: [10, 0, 10, 0],
        filename: 'Ket-qua-danh-gia.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 3 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css'] }
      };

      const worker = html2pdf().from(hiddenRef.current).set(option).toPdf();
      const pdfObj = await worker.get('pdf');

      const blob = await pdfObj.output('blob');
      const blobUrl = URL.createObjectURL(blob);

      setPdfBlob(blob);
      setPdfUrl(blobUrl);

      const file = blobToFile(blob, 'Phieu-cham-sang-kien.pdf');
      onSuccess(file);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setLoadingDownload(false);
    }
  }, [onSuccess]);

  useEffect(() => {
    if (open && !loadingInit) {
      requestAnimationFrame(() => {
        handleGeneratePdf();
      });
    }
  }, [open, loadingInit, handleGeneratePdf, data, viewData]);

  const handleDownloadClick = () => {
    if (!pdfBlob) return;

    const blobUrl = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = 'Ket-qua-danh-gia.pdf';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(blobUrl);
  };

  return (
    <FullScreenModal
      visible={open}
      onCancel={onClose}
      footer={
        <ButtonWrapper>
          <Button onClick={onClose}>Đóng</Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            loading={deferredLoading}
            onClick={handleDownloadClick}>
            Tải file
          </Button>
        </ButtonWrapper>
      }>
      <Spin spinning={deferredLoading} tip="Đang chuyển đổi PDF (vui lòng chờ)...">
        {pdfUrl ? (
          <div style={{ width: '100%', height: height - 150, background: '#f5f5f5' }}>
            <iframe title="PDF Preview" src={pdfUrl} style={{ width: '100%', height: '100%' }} />
          </div>
        ) : (
          <p></p>
        )}
      </Spin>

      <HiddenContainer>
        <A4Container ref={hiddenRef}>
          <HeaderContainer>
            <HeaderColumn>
              <p
                className="sub-title border-bottom"
                style={{ whiteSpace: 'nowrap', textTransform: 'uppercase' }}>
                ỦY BAN NHÂN DÂN{' '}
                {PGD_NAME.find((item) => item.id === user?.schools?.[0]?.pgd_id)?.label}
              </p>
              <p className="sub-title under-border">HỘI ĐỒNG XÉT SÁNG KIẾN, GIẢI PHÁP</p>
            </HeaderColumn>
            <HeaderColumn>
              <p className="sub-title" style={{ whiteSpace: 'nowrap' }}>
                CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM
              </p>
              <p className="sub-title border-bottom">Độc lập - Tự do - Hạnh phúc</p>
            </HeaderColumn>
          </HeaderContainer>

          <TitleContainer>
            <p className="main-title">PHIẾU ĐÁNH GIÁ SÁNG KIẾN, GIẢI PHÁP KINH NGHIỆM</p>
          </TitleContainer>

          <UserInfoContainer>
            <p>
              <span>Tác giả: </span>
              <span>{data?.user_fullname}</span>
            </p>
            <p>
              <span>Chức vụ: </span>
              <span>{data?.position_title}</span>
            </p>
            <p>
              <span>Đơn vị công tác: </span>
              <span>{data?.school_name}</span>
            </p>
            <p>
              <span>Tên sáng kiến: </span>
              <span>{data?.name}</span>
            </p>
          </UserInfoContainer>
          <Row gutter={[24, 24]}>
            <Col span={24}>
              <Field
                label=""
                weight="bold"
                size="large"
                inputContent={
                  <TableContainer>
                    <StyledStatisticTable
                      columns={columns}
                      dataSource={viewData}
                      bordered
                      rowKey="key"
                      pagination={false}
                      rowClassName={(record: any) =>
                        record.isTitle || record.isSummary ? 'standard-row' : ''
                      }
                    />
                  </TableContainer>
                }
              />
            </Col>

            <Col span={24}>
              <Field
                label={<span>Nhận xét của các thành viên đánh giá:</span>}
                weight="bold"
                inputContent={
                  <CheckboxContainer>
                    <Field
                      label="Về hiệu quả áp dụng:"
                      weight="normal"
                      inputContent={
                        <CommentContainer>{reviewerComment.c_hieuqua}</CommentContainer>
                      }
                    />

                    <Field
                      label="Về khả năng nhân rộng:"
                      weight="normal"
                      inputContent={
                        <CommentContainer>{reviewerComment.c_nhanrong}</CommentContainer>
                      }
                    />

                    <Field
                      label="a) Điều kiện xét, công nhận sáng kiến"
                      weight="bold"
                      inputContent={
                        <CheckboxContainer>
                          <CheckboxWithTitleForPdf
                            key="a1"
                            title="1. Có tính mới trong phạm vi cơ sở:"
                            value={reviewerComment.ca1}
                          />
                          <CheckboxWithTitleForPdf
                            key="a2"
                            title="2. Đã áp dụng hoặc áp dụng thử tại Đơn vị cơ sở: "
                            value={reviewerComment.ca2}
                          />
                          <CheckboxWithTitleForPdf
                            key="a3"
                            title="3. Khả năng mang lại lợi ích thiết thực:"
                            value={reviewerComment.ca3}
                          />
                          <CheckboxWithTitleForPdf
                            key="a4"
                            title="- Đủ điều kiện đề nghị xét, công nhận sáng kiến tại đơn vị cơ sở:"
                            value={reviewerComment.ca_du_dk}
                          />
                        </CheckboxContainer>
                      }
                    />

                    <Field
                      label="b) Điều kiện xét, công nhận hiệu quả áp dụng và khả năng nhân rộng của sáng kiến trong phạm vi cơ sở"
                      weight="bold"
                      inputContent={
                        <CheckboxContainer>
                          <CheckboxWithTitleForPdf
                            key="b1"
                            title="1. Được đề nghị công nhận sáng kiến tại Đơn vị cơ sở:"
                            value={reviewerComment.cb1}
                          />
                          <CheckboxWithTitleForPdf
                            key="b2"
                            title="2. Được áp dụng mang lại hiệu quả tại Đơn vị cơ sở:"
                            value={reviewerComment.cb2}
                          />
                          <CheckboxWithTitleForPdf
                            key="b3"
                            title="3. Có khả năng nhân rộng phạm vi cơ sở:"
                            value={reviewerComment.cb3}
                          />
                          <CheckboxWithTitleForPdf
                            key="b4"
                            title="- Đủ điều kiện xét, công nhận hiệu quả áp dụng và khả năng nhân rộng của sáng kiến trong phạm vi cơ sở:"
                            value={reviewerComment.cb_du_dk}
                          />
                        </CheckboxContainer>
                      }
                    />
                  </CheckboxContainer>
                }
              />
            </Col>
          </Row>

          <FooterContainer>
            <SignArea>
              <p className="italic" style={{ visibility: 'hidden' }}>
                Notthing
              </p>
              <p className="user-title">CHỦ TỊCH HỘI ĐỒNG</p>
            </SignArea>
            <SignArea>
              <p className="italic">
                Thành phố Hồ Chí Minh,{' '}
                {data?.updated_at ? (
                  dayjs(data.updated_at).format('[ngày] DD [tháng] MM [năm] YYYY')
                ) : (
                  <span>ngày &#160; tháng &#160; năm</span>
                )}
              </p>
              <p className="user-title">Thành viên Hội đồng</p>
              <p className="position footer">
                {isCv1 ? data?.teacher_info_3 : data?.teacher_info_4}
              </p>
            </SignArea>
          </FooterContainer>
        </A4Container>
      </HiddenContainer>
    </FullScreenModal>
  );
}

export default ModalGenerate;
